import React from 'react';
import Icon from '../../../components/AppIcon';

const RatingAnalytics = ({ averageRating, totalReviews, ratingDistribution, responseRate }) => {
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    return (
      <div className="flex items-center space-x-0.5">
        {[1, 2, 3, 4, 5]?.map((star) => {
          if (star <= fullStars) {
            return (
              <Icon
                key={star}
                name="Star"
                size={20}
                strokeWidth={2}
                className="text-accent fill-accent"
              />
            );
          } else if (star === fullStars + 1 && hasHalfStar) {
            return (
              <div key={star} className="relative">
                <Icon name="Star" size={20} strokeWidth={2} className="text-muted-foreground" />
                <div className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                  <Icon name="Star" size={20} strokeWidth={2} className="text-accent fill-accent" />
                </div>
              </div>
            );
          } else {
            return (
              <Icon
                key={star}
                name="Star"
                size={20}
                strokeWidth={2}
                className="text-muted-foreground"
              />
            );
          }
        })}
      </div>
    );
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-6 mb-8">
      <h2 className="font-heading text-xl font-semibold text-foreground mb-6">Rating Overview</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Overall Rating */}
        <div className="text-center lg:text-left">
          <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-3 lg:space-y-0 lg:space-x-4">
            <div className="text-center">
              <div className="font-heading text-5xl font-bold text-primary mb-2">
                {averageRating}
              </div>
              {renderStars(parseFloat(averageRating))}
              <p className="font-caption text-sm text-muted-foreground mt-2">
                Based on {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
              </p>
            </div>
          </div>
        </div>

        {/* Rating Distribution */}
        <div className="lg:col-span-2">
          <div className="space-y-2">
            {ratingDistribution?.map((item) => (
              <div key={item?.rating} className="flex items-center space-x-3">
                <div className="flex items-center space-x-1 w-16">
                  <span className="font-body text-sm font-medium text-foreground">{item?.rating}</span>
                  <Icon name="Star" size={14} className="text-accent fill-accent" strokeWidth={2} />
                </div>
                <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent rounded-full transition-all duration-500"
                    style={{ width: `${item?.percentage}%` }}
                  ></div>
                </div>
                <span className="font-caption text-sm text-muted-foreground w-12 text-right">
                  {item?.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Icon name="MessageSquare" size={24} className="text-primary" strokeWidth={2} />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground">{responseRate}%</p>
          <p className="font-caption text-xs text-muted-foreground">Response Rate</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Icon name="TrendingUp" size={24} className="text-success" strokeWidth={2} />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground">
            {ratingDistribution?.filter(r => r?.rating >= 4)?.reduce((sum, r) => sum + r?.count, 0)}
          </p>
          <p className="font-caption text-xs text-muted-foreground">Positive Reviews</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Icon name="Clock" size={24} className="text-warning" strokeWidth={2} />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground">2.5h</p>
          <p className="font-caption text-xs text-muted-foreground">Avg Response Time</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Icon name="ThumbsUp" size={24} className="text-accent" strokeWidth={2} />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground">95%</p>
          <p className="font-caption text-xs text-muted-foreground">Satisfaction Rate</p>
        </div>
      </div>
    </div>
  );
};

export default RatingAnalytics;